import { useState, useEffect } from 'react'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import api from '../../utils/axiosConfig'

const RequestMemberEdit = () => {
  const navigate = useNavigate()
  const { memberId } = useParams()
  const location = useLocation()
  const [member, setMember] = useState(location.state?.member || null)
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [savingDirect, setSavingDirect] = useState(false)
  const [requestedChanges, setRequestedChanges] = useState({})
  const [directFields, setDirectFields] = useState({})
  const [reason, setReason] = useState('')
  const [errors, setErrors] = useState({})
  const [directSuccess, setDirectSuccess] = useState('')
  const [states, setStates] = useState([])
  const [savingAll, setSavingAll] = useState(false)
  const [allFields, setAllFields] = useState({})

  // Direct mode for coaches/managers - all fields editable without admin approval
  const isDirectMode = location.state?.directMode || false
  const memberType = (member?.member_type || 'player').toLowerCase()

  useEffect(() => {
    if (!member && memberId) {
      fetchMemberDetails()
    }
  }, [memberId])

  useEffect(() => {
    if (isDirectMode) {
      fetchStates()
    }
  }, [isDirectMode])

  useEffect(() => {
    if (member) {
      setDirectFields({
        gender: (member.gender || '').toUpperCase() || 'M',
        blood_group: member.blood_group || '',
        height: member.height || '',
        weight: member.weight || '',
        position: member.position || '',
        address: member.address || '',
        district: member.district || '',
        pin_code: member.pin_code || '',
        primary_contact: member.primary_contact || '',
        secondary_contact: member.secondary_contact || '',
        email: member.email || '',
        aadhar_card: member.aadhar_card || '',
        passport_number: member.passport_number || ''
      })
      if (isDirectMode) {
        setAllFields({
          full_name: member.full_name || '',
          father_name: member.father_name || '',
          date_of_birth: member.date_of_birth || '',
          gender: (member.gender || '').toUpperCase() || 'M',
          state_id: member.state_id || '',
          blood_group: member.blood_group || '',
          height: member.height || '',
          weight: member.weight || '',
          position: member.position || '',
          address: member.address || '',
          district: member.district || '',
          pin_code: member.pin_code || '',
          contact: member.primary_contact || member.contact || '',
          emergency_contact: member.secondary_contact || member.emergency_contact || '',
          email: member.email || '',
          aadhar_card: member.aadhar_card || '',
          passport_number: member.passport_number || ''
        })
      }
    }
  }, [member])

  const fetchMemberDetails = async () => {
    try {
      setLoading(true)
      const response = await api.get(`/user/my-players/${memberId}`)
      if (response.data.success) {
        setMember(response.data.data)
      }
    } catch (error) {
      console.error('Error fetching member:', error)
      alert('Failed to load member details')
      navigate('/user/my-players')
    } finally {
      setLoading(false)
    }
  }

  const fetchStates = async () => {
    try {
      const response = await api.get('/user/states')
      if (response.data.success) {
        setStates(response.data.data || [])
      }
    } catch (error) {
      console.error('Error fetching states:', error)
    }
  }

  const handleAllFieldChange = (field, value) => {
    setAllFields(prev => ({ ...prev, [field]: value }))
    setDirectSuccess('')
  }

  const handleSaveAll = async () => {
    if (!allFields.full_name || !allFields.father_name || !allFields.date_of_birth) {
      alert('Name, Father\'s Name, and Date of Birth are required')
      return
    }
    setSavingAll(true)
    setDirectSuccess('')
    try {
      const formData = new FormData()
      formData.append('full_name', allFields.full_name)
      formData.append('father_name', allFields.father_name)
      formData.append('date_of_birth', allFields.date_of_birth)
      formData.append('gender', allFields.gender)
      formData.append('state_id', allFields.state_id)
      formData.append('blood_group', allFields.blood_group || '')
      formData.append('height', allFields.height || '')
      formData.append('weight', allFields.weight || '')
      formData.append('position', allFields.position || '')
      formData.append('address', allFields.address || '')
      formData.append('district', allFields.district || '')
      formData.append('pin_code', allFields.pin_code || '')
      formData.append('contact', allFields.contact || '')
      formData.append('emergency_contact', allFields.emergency_contact || '')
      formData.append('email', allFields.email || '')
      formData.append('aadhar_card', allFields.aadhar_card || '')
      formData.append('passport_number', allFields.passport_number || '')
      formData.append('_method', 'PUT')

      const response = await api.post(`/user/my-players/${member.original_id || member.id}`, formData)
      if (response.data.success) {
        setDirectSuccess('Member updated successfully!')
        setTimeout(() => navigate('/user/my-players'), 1500)
      }
    } catch (error) {
      console.error('Error saving member:', error)
      alert(error.response?.data?.message || 'Failed to update member')
    } finally {
      setSavingAll(false)
    }
  }

  const handleFieldChange = (field, value) => {
    setRequestedChanges(prev => ({
      ...prev,
      [field]: value
    }))
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }))
    }
  }

  const handleDirectFieldChange = (field, value) => {
    setDirectFields(prev => ({
      ...prev,
      [field]: value
    }))
    setDirectSuccess('')
  }

  // Save direct fields immediately (no admin approval needed)
  const handleDirectSave = async () => {
    setSavingDirect(true)
    setDirectSuccess('')
    try {
      const response = await api.post(`/user/my-players/${member.original_id || member.id}/update-fields`, {
        gender: directFields.gender,
        blood_group: directFields.blood_group,
        height: directFields.height,
        weight: directFields.weight,
        position: directFields.position,
        address: directFields.address,
        district: directFields.district,
        pin_code: directFields.pin_code,
        primary_contact: directFields.primary_contact,
        secondary_contact: directFields.secondary_contact,
        email: directFields.email,
        aadhar_card: directFields.aadhar_card,
        passport_number: directFields.passport_number
      })

      if (response.data.success) {
        setDirectSuccess('Details updated successfully!')
        if (response.data.data) {
          setMember(prev => ({ ...prev, ...response.data.data }))
        }
      }
    } catch (error) {
      console.error('Error saving details:', error)
      alert(error.response?.data?.message || 'Failed to save details')
    } finally {
      setSavingDirect(false)
    }
  }

  // Submit edit request for Name, Father's Name, DOB (admin approval needed)
  const handleSubmit = async (e) => {
    e.preventDefault()

    if (Object.keys(requestedChanges).length === 0) {
      alert('Please select at least one field to edit')
      return
    }

    if (!reason.trim() || reason.trim().length < 10) {
      setErrors({ reason: 'Please provide a reason (minimum 10 characters)' })
      return
    }

    setSubmitting(true)

    try {
      const response = await api.post('/user/member-edit-requests', {
        member_id: member.original_id || member.id,
        member_type: member.member_type || 'player',
        requested_changes: requestedChanges,
        reason: reason.trim()
      })

      if (response.data.success) {
        alert('Edit request submitted successfully! Admin will review your request.')
        navigate('/user/my-players')
      }
    } catch (error) {
      console.error('Error submitting edit request:', error)
      alert(error.response?.data?.message || 'Failed to submit edit request')
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <div className="loading-container">
        <i className="fas fa-spinner fa-spin"></i>
        <p>Loading member details...</p>
      </div>
    )
  }

  if (!member) {
    return (
      <div className="error-container">
        <i className="fas fa-exclamation-triangle"></i>
        <p>Member not found</p>
        <button onClick={() => navigate('/user/my-players')}>Go Back</button>
      </div>
    )
  }

  // Fields that require admin approval
  const requestFields = [
    { key: 'full_name', label: 'Name', type: 'text', current: member.full_name },
    { key: 'father_name', label: "Father's Name", type: 'text', current: member.father_name },
    { key: 'date_of_birth', label: 'Date of Birth', type: 'date', current: member.date_of_birth }
  ]

  // Fields that can be directly edited
  const directEditFields = [
    { key: 'gender', label: 'Gender', type: 'select', options: [{ value: 'M', label: 'Male' }, { value: 'F', label: 'Female' }] },
    { key: 'blood_group', label: 'Blood Group', type: 'select', options: ['', 'A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'] },
    { key: 'height', label: 'Height (cm)', type: 'number' },
    { key: 'weight', label: 'Weight (kg)', type: 'number' },
    { key: 'address', label: 'Address', type: 'textarea' },
    { key: 'district', label: 'District', type: 'text' },
    { key: 'pin_code', label: 'PIN Code', type: 'text' },
    { key: 'primary_contact', label: 'Primary Contact', type: 'tel' },
    { key: 'secondary_contact', label: 'Secondary Contact', type: 'tel' },
    { key: 'email', label: 'Email', type: 'email' },
    { key: 'aadhar_card', label: 'Aadhar Card', type: 'text' },
    { key: 'passport_number', label: 'Passport Number', type: 'text' }
  ]

  return (
    <div className="request-edit-page">
      <div className="page-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div className="header-content">
          <h1>Edit Member Details</h1>
          <p>Update member information</p>
        </div>
        <button className="btn-back" onClick={() => navigate('/user/my-players')}>
          <i className="fas fa-arrow-left"></i>
        </button>
      </div>

      <div className="member-summary">
        <div className="summary-card">
          <div className="card-header">
            <i className="fas fa-user-circle"></i>
            <h3>Current Member Information</h3>
          </div>
          <div className="card-body">
            <div className="info-grid">
              <div className="info-item">
                <span className="label">Name:</span>
                <span className="value">{member.full_name}</span>
              </div>
              <div className="info-item">
                <span className="label">Type:</span>
                <span className="value">{(member.member_type || 'player').charAt(0).toUpperCase() + (member.member_type || 'player').slice(1)}</span>
              </div>
              <div className="info-item">
                <span className="label">Registration ID:</span>
                <span className="value">{member.player_reg_id}</span>
              </div>
              <div className="info-item">
                <span className="label">Status:</span>
                <span className="value">{member.status}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Direct Mode for Coaches/Managers - All fields editable */}
      {isDirectMode && (
        <div className="edit-request-form">
          <div className="form-section">
            <div className="section-header">
              <i className="fas fa-pen" style={{ color: '#10b981' }}></i>
              <h3>Edit {memberType.charAt(0).toUpperCase() + memberType.slice(1)} Details</h3>
              <p>All fields can be updated directly</p>
            </div>

            {directSuccess && (
              <div style={{
                background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: '8px',
                padding: '0.75rem 1rem', marginBottom: '1rem', color: '#065f46',
                display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.875rem'
              }}>
                <i className="fas fa-check-circle"></i> {directSuccess}
              </div>
            )}

            <div className="direct-fields-grid">
              <div className="direct-field">
                <label className="direct-field-label">Full Name *</label>
                <input type="text" value={allFields.full_name || ''} onChange={e => handleAllFieldChange('full_name', e.target.value)} className="direct-field-input" required />
              </div>
              <div className="direct-field">
                <label className="direct-field-label">Father's Name *</label>
                <input type="text" value={allFields.father_name || ''} onChange={e => handleAllFieldChange('father_name', e.target.value)} className="direct-field-input" required />
              </div>
              <div className="direct-field">
                <label className="direct-field-label">Date of Birth *</label>
                <input type="date" value={allFields.date_of_birth || ''} onChange={e => handleAllFieldChange('date_of_birth', e.target.value)} className="direct-field-input" required />
              </div>
              <div className="direct-field">
                <label className="direct-field-label">Gender *</label>
                <select value={allFields.gender || ''} onChange={e => handleAllFieldChange('gender', e.target.value)} className="direct-field-input">
                  <option value="M">Male</option>
                  <option value="F">Female</option>
                </select>
              </div>
              <div className="direct-field">
                <label className="direct-field-label">State *</label>
                <select value={allFields.state_id || ''} onChange={e => handleAllFieldChange('state_id', e.target.value)} className="direct-field-input">
                  <option value="">Select State</option>
                  {states.map(s => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>
              <div className="direct-field">
                <label className="direct-field-label">Blood Group</label>
                <select value={allFields.blood_group || ''} onChange={e => handleAllFieldChange('blood_group', e.target.value)} className="direct-field-input">
                  <option value="">Select</option>
                  {['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'].map(bg => (
                    <option key={bg} value={bg}>{bg}</option>
                  ))}
                </select>
              </div>
              <div className="direct-field">
                <label className="direct-field-label">Height (cm)</label>
                <input type="number" value={allFields.height || ''} onChange={e => handleAllFieldChange('height', e.target.value)} className="direct-field-input" />
              </div>
              <div className="direct-field">
                <label className="direct-field-label">Weight (kg)</label>
                <input type="number" value={allFields.weight || ''} onChange={e => handleAllFieldChange('weight', e.target.value)} className="direct-field-input" />
              </div>
              <div className="direct-field">
                <label className="direct-field-label">Address</label>
                <textarea value={allFields.address || ''} onChange={e => handleAllFieldChange('address', e.target.value)} className="direct-field-input" rows={2} />
              </div>
              <div className="direct-field">
                <label className="direct-field-label">District</label>
                <input type="text" value={allFields.district || ''} onChange={e => handleAllFieldChange('district', e.target.value)} className="direct-field-input" />
              </div>
              <div className="direct-field">
                <label className="direct-field-label">PIN Code</label>
                <input type="text" value={allFields.pin_code || ''} onChange={e => handleAllFieldChange('pin_code', e.target.value)} className="direct-field-input" />
              </div>
              <div className="direct-field">
                <label className="direct-field-label">Primary Contact</label>
                <input type="tel" value={allFields.contact || ''} onChange={e => handleAllFieldChange('contact', e.target.value)} className="direct-field-input" />
              </div>
              <div className="direct-field">
                <label className="direct-field-label">Emergency Contact</label>
                <input type="tel" value={allFields.emergency_contact || ''} onChange={e => handleAllFieldChange('emergency_contact', e.target.value)} className="direct-field-input" />
              </div>
              <div className="direct-field">
                <label className="direct-field-label">Email</label>
                <input type="email" value={allFields.email || ''} onChange={e => handleAllFieldChange('email', e.target.value)} className="direct-field-input" />
              </div>
              <div className="direct-field">
                <label className="direct-field-label">Aadhar Card</label>
                <input type="text" value={allFields.aadhar_card || ''} onChange={e => handleAllFieldChange('aadhar_card', e.target.value)} className="direct-field-input" />
              </div>
              <div className="direct-field">
                <label className="direct-field-label">Passport Number</label>
                <input type="text" value={allFields.passport_number || ''} onChange={e => handleAllFieldChange('passport_number', e.target.value)} className="direct-field-input" />
              </div>
            </div>

            <div style={{ marginTop: '1.5rem', display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
              <button type="button" onClick={() => navigate('/user/my-players')} style={{
                padding: '10px 24px', border: '2px solid #d1d5db', borderRadius: '8px',
                background: 'white', color: '#374151', fontWeight: '600', cursor: 'pointer', fontSize: '0.95rem'
              }}>
                <i className="fas fa-times"></i> Cancel
              </button>
              <button type="button" className="btn-direct-save" onClick={handleSaveAll} disabled={savingAll}>
                {savingAll ? (
                  <><i className="fas fa-spinner fa-spin"></i> Saving...</>
                ) : (
                  <><i className="fas fa-save"></i> Save All Changes</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Player Edit Mode - Request approval for Name/DOB, direct edit for other fields */}
      {!isDirectMode && <div className="edit-request-form">
        {/* Section 1: Direct Edit Fields */}
        <div className="form-section">
          <div className="section-header">
            <i className="fas fa-pen" style={{ color: '#10b981' }}></i>
            <h3>Edit Details</h3>
            <p>These fields can be updated directly without admin approval</p>
          </div>

          {directSuccess && (
            <div style={{
              background: '#ecfdf5',
              border: '1px solid #a7f3d0',
              borderRadius: '8px',
              padding: '0.75rem 1rem',
              marginBottom: '1rem',
              color: '#065f46',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '0.875rem'
            }}>
              <i className="fas fa-check-circle"></i>
              {directSuccess}
            </div>
          )}

          <div className="direct-fields-grid">
            {directEditFields.map(field => (
              <div key={field.key} className="direct-field">
                <label className="direct-field-label">{field.label}</label>
                {field.type === 'select' ? (
                  <select
                    value={directFields[field.key] || ''}
                    onChange={(e) => handleDirectFieldChange(field.key, e.target.value)}
                    className="direct-field-input"
                  >
                    <option value="">Select {field.label}</option>
                    {field.options.filter(o => o).map(opt => {
                      const value = typeof opt === 'object' ? opt.value : opt
                      const label = typeof opt === 'object' ? opt.label : opt
                      return <option key={value} value={value}>{label}</option>
                    })}
                  </select>
                ) : field.type === 'textarea' ? (
                  <textarea
                    value={directFields[field.key] || ''}
                    onChange={(e) => handleDirectFieldChange(field.key, e.target.value)}
                    placeholder={`Enter ${field.label.toLowerCase()}`}
                    className="direct-field-input"
                    rows={2}
                  />
                ) : (
                  <input
                    type={field.type}
                    value={directFields[field.key] || ''}
                    onChange={(e) => handleDirectFieldChange(field.key, e.target.value)}
                    placeholder={`Enter ${field.label.toLowerCase()}`}
                    className="direct-field-input"
                  />
                )}
              </div>
            ))}
          </div>

          <div style={{ marginTop: '1.5rem', display: 'flex', justifyContent: 'flex-end' }}>
            <button
              type="button"
              className="btn-direct-save"
              onClick={handleDirectSave}
              disabled={savingDirect}
            >
              {savingDirect ? (
                <><i className="fas fa-spinner fa-spin"></i> Saving...</>
              ) : (
                <><i className="fas fa-save"></i> Save Details</>
              )}
            </button>
          </div>
        </div>

        {/* Section 2: Request Edit Fields (Name, Father's Name, DOB) */}
        <form onSubmit={handleSubmit}>
          <div className="form-section" style={{ border: '2px solid #fbbf24' }}>
            <div className="section-header">
              <i className="fas fa-lock" style={{ color: '#f59e0b' }}></i>
              <h3>Request Edit (Admin Approval Required)</h3>
              <p>Name, Father's Name, and Date of Birth can only be changed with admin approval</p>
            </div>

            <div className="fields-grid">
              {requestFields.map(field => (
                <div key={field.key} className="field-card">
                  <div className="field-header">
                    <input
                      type="checkbox"
                      id={`check-${field.key}`}
                      checked={!!requestedChanges[field.key]}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setRequestedChanges(prev => ({
                            ...prev,
                            [field.key]: field.current || ''
                          }))
                        } else {
                          const newChanges = { ...requestedChanges }
                          delete newChanges[field.key]
                          setRequestedChanges(newChanges)
                        }
                      }}
                    />
                    <label htmlFor={`check-${field.key}`} className="field-label">
                      {field.label}
                    </label>
                  </div>

                  <div className="field-current">
                    <span className="label">Current:</span>
                    <span className="value">{field.current || 'Not set'}</span>
                  </div>

                  {requestedChanges.hasOwnProperty(field.key) && (
                    <div className="field-new">
                      <label className="label">New Value:</label>
                      <input
                        type={field.type}
                        value={requestedChanges[field.key] || ''}
                        onChange={(e) => handleFieldChange(field.key, e.target.value)}
                        placeholder={`Enter new ${field.label.toLowerCase()}`}
                        required
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {Object.keys(requestedChanges).length > 0 && (
            <div className="form-section">
              <div className="section-header">
                <i className="fas fa-comment-alt"></i>
                <h3>Reason for Edit Request</h3>
                <p>Explain why these changes are needed</p>
              </div>
              <textarea
                className={`reason-textarea ${errors.reason ? 'error' : ''}`}
                value={reason}
                onChange={(e) => {
                  setReason(e.target.value)
                  if (errors.reason) setErrors({ ...errors, reason: '' })
                }}
                placeholder="Please provide a detailed reason for requesting these changes (minimum 10 characters)..."
                rows={4}
                required
              />
              {errors.reason && <span className="error-text">{errors.reason}</span>}
            </div>
          )}

          {Object.keys(requestedChanges).length > 0 && (
            <div className="form-actions">
              <button
                type="button"
                className="btn-cancel"
                onClick={() => { setRequestedChanges({}); setReason('') }}
                disabled={submitting}
              >
                <i className="fas fa-times"></i> Cancel
              </button>
              <button
                type="submit"
                className="btn-submit"
                disabled={submitting || Object.keys(requestedChanges).length === 0}
              >
                {submitting ? (
                  <><i className="fas fa-spinner fa-spin"></i> Submitting...</>
                ) : (
                  <><i className="fas fa-paper-plane"></i> Submit Request</>
                )}
              </button>
            </div>
          )}
        </form>
      </div>}

      <style>{`
        .request-edit-page {
          padding: 0;
          width: 100%;
          min-height: 100vh;
          background: #f3f4f6;
        }

        .page-header {
          background: linear-gradient(135deg, #3b82f6 0%, #1e40af 100%);
          padding: 2rem;
          display: flex;
          align-items: center;
          gap: 1.5rem;
          color: white;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
        }

        .btn-back {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          border: 2px solid rgba(255,255,255,0.3);
          background: rgba(255,255,255,0.1);
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          transition: all 0.2s;
          position: relative;
          z-index: 2;
        }

        .btn-back:hover {
          background: rgba(255,255,255,0.2);
          border-color: rgba(255,255,255,0.5);
        }

        .header-content {
          position: relative;
          z-index: 2;
        }

        .header-content h1 {
          margin: 0;
          font-size: 1.75rem;
          font-weight: 600;
        }

        .header-content p {
          margin: 0.25rem 0 0;
          opacity: 0.9;
          font-size: 0.95rem;
        }

        .member-summary {
          padding: 2rem;
          padding-bottom: 0;
        }

        .summary-card {
          background: white;
          border-radius: 12px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          overflow: hidden;
        }

        .card-header {
          background: linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%);
          padding: 1.25rem 1.5rem;
          display: flex;
          align-items: center;
          gap: 0.75rem;
          border-bottom: 2px solid #d1d5db;
        }

        .card-header i {
          font-size: 1.5rem;
          color: #3b82f6;
        }

        .card-header h3 {
          margin: 0;
          font-size: 1.125rem;
          font-weight: 600;
          color: #111827;
        }

        .card-body {
          padding: 1.5rem;
        }

        .info-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 1rem;
        }

        .info-item {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        }

        .info-item .label {
          font-size: 0.8125rem;
          color: #6b7280;
          font-weight: 500;
        }

        .info-item .value {
          font-size: 0.9375rem;
          color: #111827;
          font-weight: 600;
        }

        .edit-request-form {
          padding: 2rem;
        }

        .form-section {
          background: white;
          border-radius: 12px;
          padding: 1.5rem;
          margin-bottom: 1.5rem;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .section-header {
          margin-bottom: 1.5rem;
          padding-bottom: 1rem;
          border-bottom: 2px solid #f3f4f6;
        }

        .section-header i {
          margin-right: 0.5rem;
        }

        .section-header h3 {
          margin: 0 0 0.5rem 0;
          font-size: 1.125rem;
          font-weight: 600;
          color: #111827;
        }

        .section-header p {
          margin: 0;
          font-size: 0.875rem;
          color: #6b7280;
        }

        /* Direct edit fields */
        .direct-fields-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          gap: 1rem;
        }

        .direct-field {
          display: flex;
          flex-direction: column;
          gap: 0.375rem;
        }

        .direct-field-label {
          font-size: 0.8125rem;
          font-weight: 600;
          color: #374151;
        }

        .direct-field-input {
          width: 100%;
          padding: 0.625rem;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          font-size: 0.875rem;
          font-family: inherit;
          transition: all 0.2s;
        }

        .direct-field-input:focus {
          outline: none;
          border-color: #10b981;
          box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
        }

        .btn-direct-save {
          padding: 0.75rem 1.5rem;
          border-radius: 8px;
          font-weight: 600;
          font-size: 0.9375rem;
          cursor: pointer;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          border: none;
          background: #10b981;
          color: white;
        }

        .btn-direct-save:hover:not(:disabled) {
          background: #059669;
        }

        .btn-direct-save:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        /* Request fields */
        .fields-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
          gap: 1rem;
        }

        .field-card {
          border: 2px solid #e5e7eb;
          border-radius: 8px;
          padding: 1rem;
          transition: all 0.2s;
        }

        .field-card:hover {
          border-color: #f59e0b;
          box-shadow: 0 2px 4px rgba(245, 158, 11, 0.1);
        }

        .field-header {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          margin-bottom: 0.75rem;
        }

        .field-header input[type="checkbox"] {
          width: 18px;
          height: 18px;
          cursor: pointer;
        }

        .field-label {
          font-weight: 600;
          color: #374151;
          font-size: 0.9375rem;
          cursor: pointer;
        }

        .field-current {
          padding: 0.5rem;
          background: #f9fafb;
          border-radius: 4px;
          margin-bottom: 0.5rem;
          display: flex;
          justify-content: space-between;
          font-size: 0.875rem;
        }

        .field-current .label {
          color: #6b7280;
          font-weight: 500;
        }

        .field-current .value {
          color: #111827;
          font-weight: 600;
        }

        .field-new {
          margin-top: 0.75rem;
          padding-top: 0.75rem;
          border-top: 1px dashed #d1d5db;
        }

        .field-new .label {
          display: block;
          font-size: 0.8125rem;
          color: #374151;
          font-weight: 500;
          margin-bottom: 0.5rem;
        }

        .field-new input,
        .field-new select,
        .field-new textarea {
          width: 100%;
          padding: 0.625rem;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          font-size: 0.875rem;
          transition: all 0.2s;
        }

        .field-new input:focus,
        .field-new select:focus,
        .field-new textarea:focus {
          outline: none;
          border-color: #f59e0b;
          box-shadow: 0 0 0 3px rgba(245, 158, 11, 0.1);
        }

        .reason-textarea {
          width: 100%;
          padding: 0.875rem;
          border: 2px solid #d1d5db;
          border-radius: 8px;
          font-size: 0.9375rem;
          font-family: inherit;
          resize: vertical;
          transition: all 0.2s;
        }

        .reason-textarea:focus {
          outline: none;
          border-color: #f59e0b;
          box-shadow: 0 0 0 3px rgba(245, 158, 11, 0.1);
        }

        .reason-textarea.error {
          border-color: #ef4444;
        }

        .error-text {
          color: #ef4444;
          font-size: 0.8125rem;
          margin-top: 0.5rem;
          display: block;
        }

        .form-actions {
          display: flex;
          gap: 1rem;
          justify-content: flex-end;
          padding: 1.5rem;
          background: white;
          border-radius: 12px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .btn-cancel,
        .btn-submit {
          padding: 0.875rem 1.75rem;
          border-radius: 8px;
          font-weight: 600;
          font-size: 0.9375rem;
          cursor: pointer;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          border: none;
        }

        .btn-cancel {
          background: #f3f4f6;
          color: #6b7280;
        }

        .btn-cancel:hover:not(:disabled) {
          background: #e5e7eb;
          color: #374151;
        }

        .btn-submit {
          background: #f59e0b;
          color: white;
        }

        .btn-submit:hover:not(:disabled) {
          background: #d97706;
        }

        .btn-submit:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .loading-container,
        .error-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 100vh;
          gap: 1rem;
        }

        .loading-container i,
        .error-container i {
          font-size: 3rem;
          color: #3b82f6;
        }

        @media (max-width: 768px) {
          .page-header {
            padding: 1.5rem 1rem;
          }

          .member-summary,
          .edit-request-form {
            padding: 1rem;
          }

          .fields-grid,
          .direct-fields-grid {
            grid-template-columns: 1fr;
          }

          .form-actions {
            flex-direction: column-reverse;
          }

          .btn-cancel,
          .btn-submit {
            width: 100%;
            justify-content: center;
          }
        }
      `}</style>
    </div>
  )
}

export default RequestMemberEdit
